import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from 'recharts';

import { readAchievementStatistics } from '../api/endpoints';
import type { AchievementDefinitionStatistics, AchievementStatistics } from '../api/types';
import { FAMILY_LABEL } from '../components/achievementFamilies';
import { plural } from '../components/slovakPlural';
import { ChartCard } from '../components/ChartCard';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { TableFilters } from '../components/TableFilters';
import { sortRows, useSort } from '../components/useSort';
import { StatTile } from '../components/StatTile';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { useChartTheme } from '../components/useChartTheme';
import { CHART_SERIES } from '../theme/palette';

const PERIODS = [7, 30, 90];

/** Columns this table can be ordered by. Sorted in the browser — every definition is already here. */
type SortKey =
  | 'name'
  | 'family'
  | 'tier'
  | 'threshold'
  | 'holders'
  | 'shareOfActiveChildren'
  | 'firstEarnedAt'
  | 'lastEarnedAt';

export const AchievementsPage = () => {
  const chartTheme = useChartTheme();
  const [days, setDays] = useState<number>(30);
  const [onlyUnearned, setOnlyUnearned] = useState<boolean>(false);
  const [family, setFamily] = useState('');
  const sort = useSort<SortKey>('family');
  const { data, error, isLoading, refetch } = useQuery<AchievementStatistics>({
    queryKey: ['achievement-statistics', days],
    queryFn: () => readAchievementStatistics(days),
  });

  const definitions = data?.definitions ?? [];
  // Sorted and filtered **here**, because this table holds its whole list: there is no page 2 whose rows
  // this could be getting wrong (R0141).
  const VALUE: Record<SortKey, (row: AchievementDefinitionStatistics) => string | number | null> = {
    name: (row) => row.name,
    family: (row) => FAMILY_LABEL[row.family] ?? row.family,
    tier: (row) => row.tier,
    threshold: (row) => row.threshold,
    holders: (row) => row.holders,
    shareOfActiveChildren: (row) => row.shareOfActiveChildren,
    firstEarnedAt: (row) => row.firstEarnedAt,
    lastEarnedAt: (row) => row.lastEarnedAt,
  };
  const shown = sortRows(
    definitions.filter(
      (one: AchievementDefinitionStatistics) =>
        (!onlyUnearned || one.holders === 0) && (family === '' || one.family === family),
    ),
    sort.direction,
    VALUE[sort.key],
  );
  const families = [...new Set(definitions.map((one: AchievementDefinitionStatistics) => one.family))].sort();
  const familyChart = (data?.families ?? []).map((one) => ({
    ...one,
    label: FAMILY_LABEL[one.family] ?? one.family,
  }));

  return (
    <>
      <PageHeader
        title="Ocenenia"
        subtitle="Ako si stoja samotné ocenenia, nie deti. Riadok, ktorý nikto nikdy nezískal, je buď nedosiahnuteľný, alebo zbytočný — a v oboch prípadoch je to chyba v zadaní, nie v dieťati."
        action={
          <TextField
            select
            label="Obdobie"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            sx={{ minWidth: 176 }}
          >
            {PERIODS.map((option) => (
              <MenuItem key={option} value={option}>
                Posledných {option} dní
              </MenuItem>
            ))}
          </TextField>
        }
      />

      <ErrorNotice error={error} onRetry={() => void refetch()} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Ocenení celkom"
            value={data?.totals.definitions ?? '—'}
            hint={`${data?.totals.definitionsEverEarned ?? 0} už niekto získal`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Nikdy nezískané"
            value={data?.totals.definitionsNeverEarned ?? '—'}
            hint="Toto je číslo, ktoré stojí za pozornosť"
            accent={
              data && data.totals.definitionsNeverEarned > 0 ? (
                <Chip size="small" color="warning" variant="outlined" label="pozrieť" />
              ) : null
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Deti s ocenením"
            value={data ? `${data.totals.childrenWithAny} / ${data.totals.activeChildren}` : '—'}
            hint={`priemerne ${data?.totals.averagePerChildWithAny ?? 0} na dieťa`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            label="Udelené za obdobie"
            value={data?.totals.earnedInPeriod ?? '—'}
            hint={`z ${data?.totals.earnedTotal ?? 0} celkovo`}
          />
        </Grid>
      </Grid>

      {data && data.totals.definitionsNeverEarned > 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          {plural(data.totals.definitionsNeverEarned, 'ocenenie', 'ocenenia', 'ocenení')} zatiaľ nezískalo ani
          jedno dieťa. Pri novej platforme je to normálne — vyššie stupne sú na to, aby trvali. Ak tam ostane
          prvý stupeň rodiny, je zle nastavený.
        </Alert>
      )}

      {familyChart.length > 0 && (
        <ChartCard
          title="Rodiny ocenení"
          description="Koľko detí má prvý stupeň rodiny. Počíta sa prvý stupeň, nie súčet naprieč stupňami — dieťa s tromi stupňami je stále jedno dieťa."
        >
          <BarChart data={familyChart} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 96 }}>
            <CartesianGrid {...chartTheme.grid} horizontal={false} />
            <XAxis type="number" allowDecimals={false} {...chartTheme.axis} />
            <YAxis type="category" dataKey="label" width={140} {...chartTheme.axis} />
            <Tooltip {...chartTheme.tooltip} />
            <Bar dataKey="holders" name="Deti" radius={[0, 6, 6, 0]} maxBarSize={22}>
              {familyChart.map((entry, index) => (
                <Cell key={entry.family} fill={CHART_SERIES[index % CHART_SERIES.length]} />
              ))}
            </Bar>
          </BarChart>
        </ChartCard>
      )}

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Jednotlivé ocenenia</Typography>
          </Stack>

          <TableFilters>
            <TextField
              size="small"
              select
              label="Rodina"
              value={family}
              onChange={(event) => setFamily(event.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">všetky</MenuItem>
              {families.map((one: string) => (
                <MenuItem key={one} value={one}>
                  {FAMILY_LABEL[one] ?? one}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Získanie"
              value={onlyUnearned ? 'unearned' : ''}
              onChange={(event) => setOnlyUnearned(event.target.value === 'unearned')}
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="">všetky</MenuItem>
              <MenuItem value="unearned">len nezískané</MenuItem>
            </TextField>
          </TableFilters>

          <TableContainer>
            <Table size="small" sx={{ minWidth: 780 }}>
              <TableHead>
                <TableRow>
                  <SortableCell sort={sort} column="name">
                    Ocenenie
                  </SortableCell>
                  <SortableCell sort={sort} column="family">
                    Rodina
                  </SortableCell>
                  <SortableCell sort={sort} column="tier" align="right">
                    Stupeň
                  </SortableCell>
                  <SortableCell sort={sort} column="threshold" align="right">
                    Méta
                  </SortableCell>
                  <SortableCell sort={sort} column="holders" align="right">
                    Deti
                  </SortableCell>
                  <SortableCell sort={sort} column="shareOfActiveChildren" align="right">
                    Podiel detí
                  </SortableCell>
                  <SortableCell sort={sort} column="firstEarnedAt" align="right">
                    Prvýkrát
                  </SortableCell>
                  <SortableCell sort={sort} column="lastEarnedAt" align="right">
                    Naposledy
                  </SortableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? <TableSkeletonRows columns={8} /> : null}
                {!isLoading &&
                  shown.map((row: AchievementDefinitionStatistics) => (
                    <TableRow key={row.code} hover sx={row.holders === 0 ? { opacity: 0.62 } : undefined}>
                      <TableCell sx={{ fontWeight: 560 }}>
                        {row.name}
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                          {row.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{FAMILY_LABEL[row.family] ?? row.family}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.tier}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.threshold}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.holders === 0 ? (
                          <Chip size="small" color="warning" variant="outlined" label="nikdy" />
                        ) : (
                          row.holders
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.holders === 0 ? (
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            —
                          </Box>
                        ) : (
                          `${Math.round(row.shareOfActiveChildren * 100)} %`
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}
                      >
                        {row.firstEarnedAt ? new Date(row.firstEarnedAt).toLocaleDateString('sk-SK') : '—'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}
                      >
                        {row.lastEarnedAt ? new Date(row.lastEarnedAt).toLocaleDateString('sk-SK') : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {/*
              The table had no empty branch, so „len nezískané" with nothing to show rendered a header row
              over blank space — which reads as a broken page rather than as an answer (R0122). Outside the
              table, because a message centred across eight columns is not a row.
            */}
            {!isLoading && shown.length === 0 ? (
              <EmptyState
                title={onlyUnearned ? 'Každé ocenenie už niekto získal' : 'Žiadne ocenenia'}
                detail={
                  onlyUnearned
                    ? 'Filter „len nezískané" nenašiel ani jedno. To je dobrá správa, nie prázdna tabuľka.'
                    : 'Katalóg ocenení je prázdny. Skontrolujte, či prebehli migrácie.'
                }
              />
            ) : null}
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
};
